import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Row,
  Col,
  Tooltip,
  Pagination,
  Empty
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { articlesAPI } from '../utils/api';
import { moveToTrash } from '../utils/trashApi';
import useIsMobile from '../hooks/useIsMobile';
import { getArticleStatus, STATUS_LABELS } from '../utils/articleStatus';
import { statusColors } from '../styles/theme';
import dayjs from 'dayjs';
import '../styles/articles.css';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const ArticlesList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allArticles, setAllArticles] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    section: '',
    status: ''
  });
  const isMobile = useIsMobile();
  const [mobilePage, setMobilePage] = useState(1);

  // ✅ CAMBIO: Cargar artículos cuando cambia la sección (desde URL o filtro)
  useEffect(() => {
    const sectionFromURL = searchParams.get('section');

    // Si hay sección en URL y es diferente al filtro actual, actualizarla
    if (sectionFromURL !== filters.section) {
      setFilters(prev => ({ ...prev, section: sectionFromURL || '' }));
    }

    // Cargar artículos con la sección de la URL
    loadArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('section')]);

  // El buscador del Escritorio navega aquí con ?search=…; el filtro de
  // búsqueda ya existía, solo le damos esta segunda vía de entrada.
  useEffect(() => {
    const searchFromURL = searchParams.get('search');
    if (searchFromURL) {
      setFilters(prev => ({ ...prev, search: searchFromURL }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('search')]);

  // ✅ CAMBIO: Función para cargar artículos con el filtro actual
  const loadArticles = async () => {
    setLoading(true);
    try {
      // Obtener la sección desde la URL o el estado de filtros
      const currentSection = searchParams.get('section') || filters.section || null;
      const response = await articlesAPI.getArticles(currentSection);
      
      if (response.success) {
        // Filtrar artículos eliminados
        const activeArticles = response.data.filter(article => !article.deleted_at);
        setAllArticles(activeArticles);
      }
    } catch (error) {
      message.error('Error al cargar artículos');
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar secciones desde /api/articles/sections
  const loadSections = async () => {
    try {
      const response = await articlesAPI.getSections();
      if (response.success) {
        const sectionNames = Object.keys(response.data).map(sectionName => ({
          section_name: sectionName,
          article_count: response.data[sectionName].length
        }));
        setSections(sectionNames);
      }
    } catch (error) {
      console.error('Error loading sections:', error);
    }
  };

  // Mover artículo a papelera
  const handleDeleteArticle = async (articleId, articleTitle) => {
    try {
      const response = await moveToTrash(articleId);
      
      if (response.success) {
        message.success(`Artículo "${articleTitle}" movido a la papelera`);
        loadArticles();
      } else {
        message.error('Error al mover a papelera');
      }
    } catch (error) {
      console.error('Error moving to trash:', error);
      message.error('Error al mover a papelera');
    }
  };

  // Duplicar artículo
  const handleDuplicateArticle = async (article) => {
    try {
      const duplicatedArticle = {
        title: `${article.title} (copia)`,
        subtitle: article.subtitle,
        content: article.content,
        section: article.section,
        imageUrl: article.imageUrl,
        hasAudio: article.hasAudio,
        audioUrl: article.audioUrl,
        hasVideo: article.hasVideo,
        videoUrl: article.videoUrl,
        videoType: article.videoType,
        videoThumbnail: article.videoThumbnail,
        status: 'draft',
        date: new Date().toISOString()
      };
  
      const response = await articlesAPI.createArticle(duplicatedArticle);
      
      if (response.success) {
        message.success(`Artículo duplicado como borrador`);
        navigate(`/articles/edit/${response.data.id}`);
      } else {
        message.error('Error al duplicar el artículo');
      }
    } catch (error) {
      console.error('Error duplicating article:', error);
      message.error('Error al duplicar el artículo');
    }
  };

  // Aplicar filtros (búsqueda y status en frontend)
  const applyFilters = useCallback(() => {
    let filtered = [...allArticles];
  
    // Filtro por sección (ya viene filtrado del backend, pero por si acaso)
    if (filters.section) {
      filtered = filtered.filter(article => 
        article.section === filters.section
      );
    }
  
    // Filtro por status
    if (filters.status) {
      filtered = filtered.filter(article => {
        if (filters.status === 'scheduled') {
          return article.status === 'scheduled' || 
                 (article.date && dayjs(article.date).isAfter(dayjs()));
        }
        return article.status === filters.status;
      });
    }
  
    // Filtro por búsqueda
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(article => 
        article.title?.toLowerCase().includes(searchLower) ||
        article.content?.toLowerCase().includes(searchLower)
      );
    }
  
    setFilteredArticles(filtered);
  }, [allArticles, filters]);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    applyFilters();
    setMobilePage(1);
  }, [applyFilters]);

  // Limpiar filtros
  const handleClearFilters = () => {
    setFilters({
      search: '',
      section: '',
      status: ''
    });
    setSearchParams({});
  };

  // Cambiar sección y actualizar URL
  const handleSectionChange = (value) => {
    const newSection = value || '';
    setFilters(prev => ({ ...prev, section: newSection }));
    
    if (newSection) {
      setSearchParams({ section: newSection });
    } else {
      setSearchParams({});
    }
  };

  // ✅ CAMBIO: Solo cargar secciones al inicio
  useEffect(() => {
    loadSections();
  }, []);

  // Truncar texto
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    const cleanText = text.replace(/<[^>]*>/g, '');
    return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status del artículo
  const getStatusTag = (article) => {
    if (article.status === 'draft') {
      return (
        <Tag color="default" icon={<EditOutlined />}>
          Borrador
        </Tag>
      );
    }
    
    if (article.date && dayjs(article.date).isAfter(dayjs())) {
      return (
        <Tag color="orange" icon={<ClockCircleOutlined />}>
          Programada
        </Tag>
      );
    }
    
    switch (article.status) {
      case 'published':
        return <Tag color="green">Publicado</Tag>;
      case 'scheduled':
        return (
          <Tag color="orange" icon={<ClockCircleOutlined />}>
            Programada
          </Tag>
        );
      default:
        return <Tag color="blue">Activo</Tag>;
    }
  };
  // Columnas de la tabla
  const columns = [
    {
      title: '',
      dataIndex: 'imageUrl',
      key: 'image',
      width: 150,
      render: (imageUrl) => (
        <div
          style={{
            width: '160px',
            aspectRatio: '16 / 9',
            backgroundColor: '#f0f0f0',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="thumbnail"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center'
              }}
            />
          ) : (
            <span style={{ fontSize: '24px' }}>📄</span>
          )}
        </div>
      )
    },
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      width: 350,
      render: (text, record) => (
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            marginLeft: '-4px',
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'opacity 0.2s'
          }}
          onClick={() => navigate(`/articles/edit/${record.id}`)}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <Text strong style={{ fontSize: '14px', lineHeight: '1.3' }}>
            {truncateText(text, 60)}
          </Text>
          {record.content && (
            <Paragraph 
              type="secondary" 
              style={{ margin: '4px 0 0 0', fontSize: '12px' }}
            >
              {truncateText(record.content, 80)}
            </Paragraph>
          )}
        </div>
      )
    },
    {
      title: 'Sección',
      dataIndex: 'section',
      key: 'section',
      width: 120,
      render: (section) => (
        <span className={`section-tag${section ? '' : ' section-tag--empty'}`}>
          {section || 'Sin sección'}
        </span>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status, record) => getStatusTag(record)
    },
    {
      title: 'Fecha de Publicación',
      dataIndex: 'date',
      key: 'date',
      width: 160,
      sorter: (a, b) => {
        const dateA = a.date ? dayjs(a.date) : dayjs(a.created_at);
        const dateB = b.date ? dayjs(b.date) : dayjs(b.created_at);
        return dateA.valueOf() - dateB.valueOf();
      },
      defaultSortOrder: 'descend',
      render: (date, record) => {
        const isProgrammed = date && dayjs(date).isAfter(dayjs());
        const displayDate = date || record.created_at;
        
        return (
          <div>
            <Text 
              type={isProgrammed ? "warning" : "secondary"} 
              style={{ fontSize: '12px', fontWeight: isProgrammed ? 'bold' : 'normal' }}
            >
              {isProgrammed && '📅 '}
              {formatDate(displayDate)}
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" style={{ width: '100%' }} size={4}>
          <Space>
            <Tooltip title="Ver información del artículo">
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                size="small"
                onClick={() => {
                  message.info(`ID: ${record.id} | Creado: ${formatDate(record.created_at)} | Autor: ${record.author || 'Sin autor'}`);
                }}
                style={{
                  color: '#666',
                  fontSize: '16px'
                }}
              />
            </Tooltip>
            <Tooltip title="Eliminar artículo">
              <Popconfirm
                title="¿Mover a papelera?"
                description={`"${truncateText(record.title, 40)}" se moverá a la papelera`}
                onConfirm={() => handleDeleteArticle(record.id, record.title)}
                okText="Sí, eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  className="row-danger-btn"
                  style={{ fontSize: '16px' }}
                />
              </Popconfirm>
            </Tooltip>
          </Space>

          <Space style={{ width: '100%' }}>
            <Button
              size="small"
              icon={<CopyOutlined />}
              className="btn-duplicar cms-btn-grow"
              onClick={() => {
                handleDuplicateArticle(record);
              }}
            >
              Duplicar
            </Button>

            <Button
              type="primary"
              size="small"
              className="btn-editar cms-btn-grow"
              onClick={() => navigate(`/articles/edit/${record.id}`)}
            >
              Editar
            </Button>
          </Space>
        </Space>
      )
    }
  ];

  // ---- Vista móvil: tarjetas apiladas (misma data y mismos handlers) ----
  const MOBILE_PAGE_SIZE = 10;

  // La tabla ordena por fecha descendente por defecto; las tarjetas replican
  // ese mismo orden para que la lista no cambie al girar el móvil.
  const sortByDateDesc = (list) =>
    [...list].sort((a, b) => {
      const dateA = a.date ? dayjs(a.date) : dayjs(a.created_at);
      const dateB = b.date ? dayjs(b.date) : dayjs(b.created_at);
      return dateB.valueOf() - dateA.valueOf();
    });

  const renderArticleCard = (article) => {
    const status = getArticleStatus(article);
    const palette = statusColors[status] || statusColors.draft;
    const displayDate = article.date || article.created_at;

    return (
      <div className="cms-card" key={article.id}>
        <div
          className="cms-card-media"
          onClick={() => navigate(`/articles/edit/${article.id}`)}
        >
          {article.imageUrl ? (
            <img src={article.imageUrl} alt="" />
          ) : (
            <span className="cms-card-media-empty">📄</span>
          )}
        </div>

        <div
          className="cms-card-title cms-card-title--link"
          onClick={() => navigate(`/articles/edit/${article.id}`)}
        >
          {article.title}
        </div>
        {(article.subtitle || article.content) && (
          <div className="cms-card-sub">
            {truncateText(article.subtitle || article.content, 90)}
          </div>
        )}

        <div className="cms-card-chips">
          <span className={`section-tag${article.section ? '' : ' section-tag--empty'}`}>
            {article.section || 'Sin sección'}
          </span>
          <span className="status-tag" style={{ background: palette.bg, color: palette.fg }}>
            {STATUS_LABELS[status]}
          </span>
          <span className="cms-card-date">{formatDate(displayDate)}</span>
        </div>

        <div className="cms-card-actions">
          <Button
            type="primary"
            icon={<EditOutlined />}
            className="btn-editar cms-btn-grow"
            onClick={() => navigate(`/articles/edit/${article.id}`)}
          >
            Editar
          </Button>
          <Button
            icon={<CopyOutlined />}
            className="btn-duplicar cms-btn-grow"
            onClick={() => handleDuplicateArticle(article)}
          >
            Duplicar
          </Button>
          <Popconfirm
            title="¿Mover a papelera?"
            description={`"${truncateText(article.title, 40)}" se moverá a la papelera`}
            onConfirm={() => handleDeleteArticle(article.id, article.title)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} className="btn-eliminar cms-btn-icon" aria-label="Eliminar" />
          </Popconfirm>
        </div>
      </div>
    );
  };

  return (
    <div
      className="articles-page"
      style={{
        padding: '24px',
        background: '#f0f2f5',
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Row className="cms-header-row" justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space className="page-title-group">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/dashboard')}
            >
              Escritorio
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              Gestión de Artículos
            </Title>
          </Space>
          <Text type="secondary" className="results-text">
            {filteredArticles.length} de {allArticles.length} artículos
            {filters.section && ` - Sección: ${filters.section}`}
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            className="cms-page-header-action"
            onClick={() => navigate('/articles/new')}
          >
            Nuevo Artículo
          </Button>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="filter-section" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Buscar por título o contenido..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="Filtrar por sección"
              className="filter-select"
              style={{ width: 200 }}
              value={filters.section || undefined}
              onChange={handleSectionChange}
              allowClear
            >
              {sections.map(section => (
                <Option key={section.section_name} value={section.section_name}>
                  {section.section_name} ({section.article_count})
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="Filtrar por estado"
              className="filter-select"
              style={{ width: 160 }}
              value={filters.status || undefined}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              allowClear
            >
              <Option value="published">Publicado</Option>
              <Option value="scheduled">Programada</Option>
              <Option value="draft">Borrador</Option>
            </Select>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleClearFilters}
              >
                Limpiar
              </Button>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadArticles}
                loading={loading}
              >
                Actualizar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Artículos: tabla en escritorio, tarjetas apiladas en móvil.
          Ambas vistas comparten filteredArticles y los mismos handlers. */}
      {isMobile ? (
        <div className="cms-cards">
          {loading ? (
            <Card loading />
          ) : filteredArticles.length === 0 ? (
            <Card>
              <Empty description="No hay artículos que coincidan con los filtros" />
            </Card>
          ) : (
            <>
              {sortByDateDesc(filteredArticles)
                .slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE)
                .map(renderArticleCard)}
              <Pagination
                className="cms-cards-pagination"
                simple
                current={mobilePage}
                pageSize={MOBILE_PAGE_SIZE}
                total={filteredArticles.length}
                onChange={(page) => {
                  setMobilePage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </>
          )}
        </div>
      ) : (
        <Card>
          <Table
            columns={columns}
            dataSource={filteredArticles}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} artículos`
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      )}
    </div>
  );
};

export default ArticlesList;